import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Chip,
  TextField,
  Snackbar,
  Alert,
  MenuItem
} from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import api from '../../utils/api';
import { Counsellor } from '../../types/counsellor';
import PersonalInformation from './PersonalInformation';
import ProfessionalInformation from './ProfessionalInformation';
import DocumentsInformation from './DocumentsInformation';
import EducationInformation from './EducationInformation';
import RefereeInformation from './RefereeInformation';  
import ClientList from './ClientList';
import { formatDateTime } from '../../utils/dateUtils';
import { getCounsellorStatus, getStatusChipStyles } from '../../utils/counsellorUtils';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from '../../context/AuthContext';
import LegalInfoManagement from '../admin/LegalInfoManagement';
import TrustIDVerification from '../admin/TrustIDVerification';
import CredentialReactivationReview from '../admin/CredentialReactivationReview';
import DbsManagement from './DbsManagement';
import AdminNotes from './AdminNotes';

const CounsellorDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const [counsellor, setCounsellor] = useState<Counsellor | null>(null);
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalFirstName, setLegalFirstName] = useState('');
  const [legalLastName, setLegalLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [address, setAddress] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [addressLine3, setAddressLine3] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('United Kingdom');
  const [gender, setGender] = useState('');
  
  // Education editing state
  const [isEditingEducation, setIsEditingEducation] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [learningInstitution, setLearningInstitution] = useState('');
  const [qualificationType, setQualificationType] = useState('');
  const [hoursCompleted, setHoursCompleted] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [completionDate, setCompletionDate] = useState('');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [additionalQualifications, setAdditionalQualifications] = useState<any[]>([]);
  
  const [searchParams] = useSearchParams();
  const VALID_TABS = ['personal', 'professional', 'education', 'references', 'documents', 'clients', 'dbs'];
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return t && VALID_TABS.includes(t) ? t : 'personal';
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [interviewDate, setInterviewDate] = useState<Date | null>(null);
  const [interviewerUserId, setInterviewerUserId] = useState('');
  const [interviewers, setInterviewers] = useState<Array<{ _id: string; firstName: string; lastName: string; email: string }>>([]);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [interviewOutcome, setInterviewOutcome] = useState<'successful' | 'unsuccessful' | null>(null);
  const [interviewNotes, setInterviewNotes] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [openExtendTokenDialog, setOpenExtendTokenDialog] = useState(false);
  const [newTokenExpiry, setNewTokenExpiry] = useState<Date | null>(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendCategory, setSuspendCategory] = useState<'dbs' | 'insurance' | 'investigation' | 'other'>('dbs');
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) {
      setError('No counsellor ID provided');
      return;
    }
    
    try {
      setLoading(true);
      const [counsellorResponse, interviewersResponse] = await Promise.all([
        api.get(`/api/counsellors/${id}`),
        api.get('/api/users/administrators')
      ]);
      
      const counsellorData = counsellorResponse.data;
      setCounsellor(counsellorData);
      setInterviewers(interviewersResponse.data || []);
      
      // Set initial form values from either main fields or application data
      if (counsellorData) {
        const personalData = counsellorData.application?.personal;
        setFirstName(counsellorData.firstName || personalData?.firstName || '');
        setLastName(counsellorData.lastName || personalData?.lastName || '');
        setLegalFirstName(counsellorData.legalFirstName || '');
        setLegalLastName(counsellorData.legalLastName || '');
        setEmail(counsellorData.email || personalData?.email || '');
        setPhone(counsellorData.phone || personalData?.phone || '');
        setDob(counsellorData.dob || personalData?.dob || '');
        setAddress(counsellorData.address || personalData?.address || '');
        setAddressLine1(counsellorData.addressLine1 || personalData?.addressLine1 || '');
        setAddressLine2(counsellorData.addressLine2 || personalData?.addressLine2 || '');
        setAddressLine3(counsellorData.addressLine3 || personalData?.addressLine3 || '');
        setCity(counsellorData.city || personalData?.city || '');
        setCounty(counsellorData.county || personalData?.county || '');
        setPostcode(counsellorData.postcode || personalData?.postcode || '');
        setCountry(personalData?.country || 'United Kingdom');
        setGender(counsellorData.gender || personalData?.gender || '');
        
        // Set education data
        const educationData = counsellorData.application?.education;
        if (educationData) {
          setCourseName(educationData.courseName || '');
          setLearningInstitution(educationData.learningInstitution || '');
          setQualificationType(educationData.qualificationType || '');
          setHoursCompleted(educationData.hoursCompleted || '');
          setInProgress(educationData.inProgress || false);
          setCompletionDate(educationData.completionDate || '');
          setExpectedCompletionDate(educationData.expectedCompletionDate || '');
          setAdditionalQualifications(educationData.qualifications || []);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error loading counsellor details');
    } finally {
      setLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!id) {
    return (
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="bg-white rounded-lg shadow-lg p-6 min-h-[calc(100vh-4rem)]">
          <Typography color="error">No counsellor ID provided</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/counsellors')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Back to Counsellors
          </Button>
        </div>
      </div>
    );
  }

  if (error || !counsellor) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8 overflow-y-scroll">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center gap-4">
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(239, 68, 68, 0.9)', 
                fontWeight: 600,
                textAlign: 'center'
              }}
            >
              {error || 'Counsellor not found'}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/counsellors')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minWidth: '160px',
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Back to Counsellors
            </Button>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8 overflow-y-scroll">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.8)' }} />
          </div>
        </div>
      </ThemeBackground>
    );
  }

  const handleError = (error: any) => {
    console.error('Error:', error);
    setSnackbar({
      open: true,
      message: error.response?.data?.error || 'An error occurred',
      severity: 'error'
    });
  };

  const handleSavePersonal = async () => {
    try {
      const response = await api.put(`/api/counsellors/${id}`, {
        firstName,
        lastName,
        legalFirstName,
        legalLastName,
        email,
        phone,
        dob,
        addressLine1,
        addressLine2,
        addressLine3,
        city,
        county,
        postcode
      });
      
      setCounsellor(response.data);
      setIsEditingPersonal(false);
      setError('');
      setSnackbar({
        open: true,
        message: 'Personal information updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving personal information:', err);
      setError('Failed to save personal information');
      setSnackbar({
        open: true,
        message: 'Failed to save personal information',
        severity: 'error'
      });
    }
  };

  const handleSaveEducation = async () => {
    try {
      const response = await api.put(`/api/counsellors/${id}/education`, {
        courseName,
        learningInstitution,
        qualificationType,
        hoursCompleted,
        inProgress,
        completionDate: inProgress ? null : completionDate,
        expectedCompletionDate: inProgress ? expectedCompletionDate : null,
        qualifications: additionalQualifications
      });
      
      setCounsellor(response.data);
      setIsEditingEducation(false);
      setError('');
      setSnackbar({
        open: true,
        message: 'Education information updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving education information:', err);
      setError('Failed to save education information');
      setSnackbar({
        open: true,
        message: 'Failed to save education information',
        severity: 'error'
      });
    }
  };


  const handleScheduleInterview = async () => {
    try {
      if (!id || !interviewDate || !interviewerUserId) {
        console.error('Missing required fields for scheduling interview');
        return;
      }

      // Use the new Interview API endpoint with Google Meet integration
      await api.post(`/api/interviews`, {
        counsellorId: id,
        scheduledDate: interviewDate.toISOString(),
        interviewerId: interviewerUserId
      });

      // Update counsellor status
      const response = await api.get(`/api/counsellors/${id}`);
      setCounsellor(response.data);
      
      setOpenScheduleDialog(false);
      setInterviewDate(null);
      setInterviewerUserId('');
    } catch (err) {
      console.error('Error scheduling interview:', err);
      handleError(err);
    }
  };


  const handleCloseCompleteDialog = () => {
    setOpenCompleteDialog(false);
    setInterviewOutcome(null);
    setInterviewNotes('');
  };


  const handleCompleteInterview = async () => {
    try {
      if (!interviewOutcome) {
        setSnackbar({
          open: true,
          message: 'Please select an interview outcome',
          severity: 'warning'
        });
        return;
      }

      if (!counsellor || !['Interview Scheduled', 'Interview Completed'].includes(counsellor.applicationStatus ?? '')) {
        setSnackbar({
          open: true,
          message: 'Cannot record an interview decision. Counsellor must be in "Interview Scheduled" or "Interview Completed" status.',
          severity: 'warning'
        });
        return;
      }

      // Map successful/unsuccessful to Accepted/Rejected
      const applicationStatus = interviewOutcome === 'successful' ? 'Accepted' : 'Rejected';

      const response = await api.post(`/api/counsellors/${id}/complete-interview`, {
        interviewNotes: interviewNotes,
        applicationStatus
      });

      setCounsellor(response.data.counsellor);
      setOpenCompleteDialog(false);
      setInterviewNotes('');
      setInterviewOutcome(null);

      if (interviewOutcome === 'successful') {
        // Show success message about account creation and email
        setSnackbar({
          open: true,
          message: 'Interview completed successfully. A user account has been created for the counsellor and an email has been sent with their login details.',
          severity: 'success'
        });
      } else {
        // If unsuccessful, redirect to offboarding
        setSnackbar({
          open: true,
          message: 'Interview marked as unsuccessful. Redirecting to offboarding...',
          severity: 'info'
        });
        setTimeout(() => navigate(`/offboard/counsellor/${id}`), 2000);
      }
    } catch (error) {
      handleError(error);
    }
  };


  const handleReissueOnboardingEmail = async () => {
    try {
      await api.post(`/api/counsellors/${id}/reissue-onboarding-email`);
      setSnackbar({
        open: true,
        message: 'Onboarding email has been resent successfully with a new password reset link!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error reissuing onboarding email:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to reissue onboarding email',
        severity: 'error'
      });
    }
  };

  const handleRequestLegalInfo = async () => {
    try {
      const response = await api.post(`/api/counsellors/${id}/request-legal-update`, {});
      if (response.data?.counsellor) {
        setCounsellor(response.data.counsellor);
      } else {
        const refreshed = await api.get(`/api/counsellors/${id}`);
        setCounsellor(refreshed.data);
      }
      setSnackbar({
        open: true,
        message: 'Legal information form sent to the counsellor!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error requesting legal info:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send legal information form',
        severity: 'error'
      });
    }
  };

  const handleCompleteOnboarding = async () => {
    try {
      // Check if legal information has been validated
      if (!counsellor?.legalInfoValidated) {
        setSnackbar({
          open: true,
          message: 'Legal information must be validated before completing onboarding. Please review and approve the legal information first.',
          severity: 'warning'
        });
        setActiveTab('legal'); // Switch to legal tab
        return;
      }

      // Check if all individual validation checks are confirmed
      const validationChecks = counsellor?.legalInfoValidationChecks;
      if (!validationChecks) {
        setSnackbar({
          open: true,
          message: 'Legal validation checks are missing. Please validate all legal information.',
          severity: 'warning'
        });
        setActiveTab('legal');
        return;
      }

      // Ensure all required validations are checked
      const requiredChecks = [
        'identityVerified',
        'dbsVerified',
        'nationalityVerified',
        'workPermitVerified',
        'insuranceVerified',
        'convictionsVerified'
      ];
      
      const missingValidations = requiredChecks.filter(check => !validationChecks[check as keyof typeof validationChecks]);
      
      if (missingValidations.length > 0) {
        const formattedMissing = missingValidations.map(check => 
          check.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
        ).join(', ');
        
        setSnackbar({
          open: true,
          message: `The following validations are missing: ${formattedMissing}. Please complete all validations before proceeding.`,
          severity: 'warning'
        });
        setActiveTab('legal');
        return;
      }

      const response = await api.post(`/api/counsellors/${id}/complete-onboarding`);
      // Update the counsellor state with the new data
      setCounsellor(prevState => ({
        ...prevState,
        ...response.data.counsellor,
        applicationStatus: 'Active' // Explicitly set the status
      }));
      setSnackbar({
        open: true,
        message: 'Onboarding completed successfully!',
        severity: 'success'
      });
    } catch (error) {
      handleError(error);
    }
  };


  const handleBack = () => {
    if (isAdmin) {
      navigate('/counsellors');
    } else {
      navigate('/counsellor-dashboard');
    }
  };

  const renderActionButtons = () => {
    // Status-specific buttons
    let statusButtons = null;
    
    if (counsellor.applicationStatus === 'Submitted' || counsellor.applicationStatus === 'Application Submitted') {
      statusButtons = (
        <Button
          variant="contained"
          onClick={() => setOpenScheduleDialog(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            minWidth: { xs: 'auto', sm: '160px' },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            backgroundColor: '#9c27b0',
            '&:hover': {
              backgroundColor: '#7b1fa2'
            }
          }}
        >
          Schedule Interview
        </Button>
      );
    } else if (counsellor.applicationStatus === 'Interview Scheduled') {
      statusButtons = (
        <Button
          variant="contained"
          onClick={() => setOpenCompleteDialog(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            minWidth: { xs: 'auto', sm: '160px' },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            backgroundColor: '#2196f3',
            '&:hover': {
              backgroundColor: '#1976d2'
            }
          }}
        >
          Complete Interview
        </Button>
      );
    } else if (counsellor.applicationStatus === 'Interview Completed') {
      statusButtons = (
        <Button
          variant="contained"
          onClick={() => setOpenCompleteDialog(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            minWidth: '160px',
            backgroundColor: '#2196f3',
            '&:hover': {
              backgroundColor: '#1976d2'
            }
          }}
        >
          Record Decision
        </Button>
      );
    } else if (counsellor.applicationStatus === 'Accepted' || counsellor.applicationStatus === 'Legal Information Requested' || counsellor.applicationStatus === 'Legal Info Rejected' || counsellor.applicationStatus === 'Ready For Onboarding') {
      statusButtons = (
        <>
          <Button
            variant="contained"
            onClick={handleRequestLegalInfo}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: { xs: 'auto', sm: '200px' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              backgroundColor: '#7C3AED',
              '&:hover': {
                backgroundColor: '#6D28D9'
              }
            }}
          >
            {counsellor.applicationStatus === 'Accepted' ? 'Send Legal Form' : 'Resend Legal Form'}
          </Button>
          <Button
            variant="contained"
            onClick={handleReissueOnboardingEmail}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: { xs: 'auto', sm: '200px' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              backgroundColor: '#3B82F6',
              '&:hover': {
                backgroundColor: '#2563EB'
              }
            }}
          >
            Reissue Onboarding Email
          </Button>
        </>
      );
    }

    // Always show Archive Counsellor button for admins (unless already archived)
    return (
      <>
        {isAdmin && (
          <>
            {statusButtons}
            {!counsellor.isArchived && counsellor.applicationStatus === 'Suspended' && (
              <Button
                variant="contained"
                onClick={handleUnsuspend}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '160px',
                  backgroundColor: '#10B981',
                  '&:hover': {
                    backgroundColor: '#059669'
                  }
                }}
              >
                Reactivate Counsellor
              </Button>
            )}
            {!counsellor.isArchived &&
              counsellor.applicationStatus !== 'Suspended' &&
              counsellor.applicationStatus !== 'Offboarded' && (
              <Button
                variant="contained"
                onClick={() => setSuspendDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '160px',
                  backgroundColor: '#f59e0b',
                  '&:hover': {
                    backgroundColor: '#d97706'
                  }
                }}
              >
                Suspend Counsellor
              </Button>
            )}
            {!counsellor.isArchived && (
              <Button
                variant="contained"
                onClick={() => {
                  console.log('Navigating to offboard with ID:', id);
                  navigate(`/offboard/counsellor/${id}`);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '160px',
                  backgroundColor: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#b91c1c'
                  }
                }}
              >
                Archive Counsellor
              </Button>
            )}
          </>
        )}
      </>
    );
  };

  // Ensure interviewers is initialized as an empty array if undefined
  const safeInterviewers = Array.isArray(interviewers) ? interviewers : [];

  const handleDateChange = (date: Date | null) => {
    setInterviewDate(date);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInterviewNotes(e.target.value);
  };

  const handleExtendToken = async () => {
    try {
      if (!newTokenExpiry) {
        setSnackbar({
          open: true,
          message: 'Please select a new expiry date',
          severity: 'warning'
        });
        return;
      }

      await api.put(`/api/counsellors/${id}/extend-token`, {
        newExpiry: newTokenExpiry.toISOString()
      });

      // Refresh counsellor data
      const response = await api.get(`/api/counsellors/${id}`);
      setCounsellor(response.data);

      setOpenExtendTokenDialog(false);
      setNewTokenExpiry(null);
      setSnackbar({
        open: true,
        message: 'Application form expiry date extended successfully!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error extending token:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to extend token expiry',
        severity: 'error'
      });
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      setSnackbar({ open: true, message: 'Please enter a suspension reason', severity: 'warning' });
      return;
    }
    setSuspending(true);
    try {
      const res = await api.post(`/api/counsellors/${id}/suspend`, {
        reason: suspendReason.trim(),
        category: suspendCategory
      });
      setSuspendDialogOpen(false);
      setSuspendReason('');
      setSnackbar({
        open: true,
        message: `Counsellor suspended${res.data?.cancelledAppointments ? ` — ${res.data.cancelledAppointments} future appointment(s) cancelled` : ''}`,
        severity: 'success'
      });
      const response = await api.get(`/api/counsellors/${id}`);
      setCounsellor(response.data);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to suspend counsellor', severity: 'error' });
    } finally {
      setSuspending(false);
    }
  };

  const handleUnsuspend = async () => {
    try {
      await api.post(`/api/counsellors/${id}/unsuspend`, {});
      setSnackbar({ open: true, message: 'Counsellor reactivated', severity: 'success' });
      const response = await api.get(`/api/counsellors/${id}`);
      setCounsellor(response.data);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to reactivate counsellor', severity: 'error' });
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 overflow-y-scroll">
        <div className="glass-card p-3 sm:p-4 md:p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-4 sm:mb-6">
              <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-2 sm:space-y-4 w-full sm:w-auto">
                  {/* Primary Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                    <Button
                      variant="contained"
                      onClick={handleBack}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: { xs: 'auto', sm: '160px' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      {isAdmin ? 'Back to Counsellors' : 'Back to Dashboard'}
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-left sm:text-right w-full sm:w-auto">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      letterSpacing: '0.5px'
                    }}
                  >
                    Counsellor Details
                  </Typography>
                  {counsellor && (
                    <Typography 
                      variant="subtitle1"
                      className="theme-text-secondary"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' }
                      }}
                    >
                      {counsellor.firstName} {counsellor.lastName}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Display */}
          <div className="mt-4 sm:mt-6 md:mt-8 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 glass-card-light p-3 sm:p-4 rounded-lg">
              <div className="flex items-center">
                <div>
                  <Typography variant="subtitle2" className="theme-text-secondary" gutterBottom>
                    Counsellor Journey Status
                  </Typography>
                  <Chip
                    label={getCounsellorStatus(counsellor)}
                    sx={{
                      height: '32px',
                      borderRadius: '16px',
                      fontWeight: 600,
                      ...getStatusChipStyles(counsellor)
                    }}
                  />
                  {counsellor.interviewDate && counsellor.applicationStatus === 'Interview Scheduled' && (
                    <Typography 
                      variant="caption"
                      className="theme-text-secondary"
                      sx={{ 
                        display: 'block',
                        mt: 1
                      }}
                    >
                      Interview scheduled for {formatDateTime(counsellor.interviewDate)}
                    </Typography>
                  )}
                  {counsellor.applicationStatus === 'Suspended' && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: '#fca5a5',
                        fontWeight: 600
                      }}
                    >
                      Suspended{counsellor.suspendedDate ? ` on ${new Date(counsellor.suspendedDate).toLocaleDateString('en-GB')}` : ''}
                      {counsellor.suspensionReason ? ` — ${counsellor.suspensionReason}` : ''}
                    </Typography>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                {renderActionButtons()}
              </div>
            </div>
          </div>

          {/* Application/Onboarding/Legal Form Expiry Section */}
          {isAdmin && (
            (['Registered Interest', 'Application Issued', 'Application In Progress', 'Pending', 'Started'].includes(counsellor.applicationStatus ?? '') && counsellor.applicationExpiryDate) ||
            (counsellor.applicationStatus === 'Pending Self-Completion' && counsellor.onboardingTokenExpiry) ||
            (['Legal Information Requested', 'Legal Info Requested', 'Legal Info Update Requested', 'Legal Info Rejected'].includes(counsellor.applicationStatus ?? '') && counsellor.legalFormExpiryDate)
          ) && (
            <div className="mb-4 sm:mb-6">
              <div className="glass-card-light p-3 sm:p-4 rounded-lg">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    {(() => {
                      const isOnboarding = counsellor.applicationStatus === 'Pending Self-Completion';
                      const isLegal = ['Legal Information Requested', 'Legal Info Requested', 'Legal Info Update Requested', 'Legal Info Rejected'].includes(counsellor.applicationStatus ?? '');
                      const expiryDate = isOnboarding
                        ? counsellor.onboardingTokenExpiry
                        : isLegal
                          ? counsellor.legalFormExpiryDate
                          : counsellor.applicationExpiryDate;
                      const label = isOnboarding
                        ? 'Onboarding Form Expiry'
                        : isLegal
                          ? 'Legal Form Expiry'
                          : 'Application Form Expiry';
                      return (
                        <>
                          <Typography variant="subtitle2" className="theme-text-secondary" gutterBottom>
                            {label}
                          </Typography>
                          <Typography variant="body1" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                            {expiryDate ? new Date(expiryDate).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            }) : 'N/A'}
                          </Typography>
                          {expiryDate && new Date(expiryDate) < new Date() && (
                            <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                              ⚠ Expired
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const expiryDate = counsellor.applicationStatus === 'Pending Self-Completion'
                        ? counsellor.onboardingTokenExpiry
                        : ['Legal Information Requested', 'Legal Info Requested', 'Legal Info Update Requested', 'Legal Info Rejected'].includes(counsellor.applicationStatus ?? '')
                          ? counsellor.legalFormExpiryDate
                          : counsellor.applicationExpiryDate;
                      setNewTokenExpiry(expiryDate ? new Date(expiryDate) : new Date());
                      setOpenExtendTokenDialog(true);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Extend Expiry
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mt-4 sm:mt-6 border-b border-white/20">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveTab('personal')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'personal'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                Personal
              </button>
              <button
                onClick={() => setActiveTab('professional')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'professional'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                Professional
              </button>
              <button
                onClick={() => setActiveTab('education')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'education'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                Education
              </button>
{isAdmin && (
              <button
                onClick={() => setActiveTab('references')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'references'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                References
              </button>
              )}
              <button
                onClick={() => setActiveTab('documents')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'documents'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                Documents
              </button>
{isAdmin && (
              <button
                onClick={() => setActiveTab('clients')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'clients'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                Client List
              </button>
              )}
              <button
                onClick={() => setActiveTab('dbs')}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                  ${activeTab === 'dbs'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }
                `}
              >
                DBS Status
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setActiveTab('legal')}
                    className={`
                      whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                      ${activeTab === 'legal'
                        ? 'border-blue-400 text-blue-400'
                        : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                      }
                    `}
                  >
                    Legal
                  </button>
                  <button
                    onClick={() => setActiveTab('notes')}
                    className={`
                      whitespace-nowrap py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors
                      ${activeTab === 'notes'
                        ? 'border-blue-400 text-blue-400'
                        : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                      }
                    `}
                  >
                    Notes
                  </button>
                </>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'personal' && (
              <PersonalInformation
                counsellor={counsellor}
                isEditingPersonal={isEditingPersonal}
                firstName={firstName}
                lastName={lastName}
                legalFirstName={legalFirstName}
                legalLastName={legalLastName}
                email={email}
                phone={phone}
                dob={dob}
                address={address}
                addressLine1={addressLine1}
                addressLine2={addressLine2}
                addressLine3={addressLine3}
                city={city}
                county={county}
                postcode={postcode}
                country={country}
                gender={gender}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setLegalFirstName={setLegalFirstName}
                setLegalLastName={setLegalLastName}
                setEmail={setEmail}
                setPhone={setPhone}
                setDob={setDob}
                setAddress={setAddress}
                setAddressLine1={setAddressLine1}
                setAddressLine2={setAddressLine2}
                setAddressLine3={setAddressLine3}
                setCity={setCity}
                setCounty={setCounty}
                setPostcode={setPostcode}
                setCountry={setCountry}
                setGender={setGender}
                handleSavePersonal={handleSavePersonal}
                setIsEditingPersonal={setIsEditingPersonal}
              />
            )}
            {activeTab === 'professional' && (
              <ProfessionalInformation
                counsellor={counsellor}
              />
            )}
            {activeTab === 'education' && (
              <EducationInformation
                counsellor={counsellor}
                isEditingEducation={isEditingEducation}
                courseName={courseName}
                learningInstitution={learningInstitution}
                qualificationType={qualificationType}
                hoursCompleted={hoursCompleted}
                inProgress={inProgress}
                completionDate={completionDate}
                expectedCompletionDate={expectedCompletionDate}
                setCourseName={setCourseName}
                setLearningInstitution={setLearningInstitution}
                setQualificationType={setQualificationType}
                setHoursCompleted={setHoursCompleted}
                setInProgress={setInProgress}
                setCompletionDate={setCompletionDate}
                setExpectedCompletionDate={setExpectedCompletionDate}
                additionalQualifications={additionalQualifications}
                setAdditionalQualifications={setAdditionalQualifications}
                handleSaveEducation={handleSaveEducation}
                setIsEditingEducation={setIsEditingEducation}
              />
            )}
            {activeTab === 'references' && (
              <RefereeInformation
                counsellor={counsellor}
                isAdmin={isAdmin}
              />
            )}
            {activeTab === 'documents' && (
              <DocumentsInformation
                counsellor={counsellor}
              />
            )}
            {activeTab === 'legal' && isAdmin && counsellor && (
              <>
                <TrustIDVerification
                  counsellorId={counsellor._id}
                  trustIdData={counsellor.trustIdVerification}
                  onUpdate={fetchData}
                />
                <LegalInfoManagement
                  counsellorId={counsellor._id}
                  counsellorData={counsellor}
                  onRefresh={fetchData}
                />
                <Box sx={{ mt: 3 }}>
                  <CredentialReactivationReview
                    counsellorId={counsellor._id}
                    onUpdate={fetchData}
                  />
                </Box>
              </>
            )}
            {activeTab === 'notes' && isAdmin && counsellor && (
              <AdminNotes
                counsellor={counsellor}
                onUpdate={fetchData}
              />
            )}
            {activeTab === 'clients' && counsellor && (
              <ClientList counsellorId={counsellor._id} />
            )}
            {activeTab === 'dbs' && counsellor && (
              <DbsManagement
                counsellorId={counsellor._id}
                token={user?.token || ''}
                refreshCounsellor={fetchData}
              />
            )}
          </div>
        </div>
      </div>
      {/* Interview Scheduling Dialog */}
      <Dialog 
        open={openScheduleDialog}
        onClose={() => setOpenScheduleDialog(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            p: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto',
          fontWeight: 700,
          fontSize: '1.5rem',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          Schedule Interview
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: 'transparent',
          pt: 3 
        }}>
          <div className="space-y-4">
            <div>
              <Typography variant="subtitle2" className="theme-text-secondary" sx={{ mb: 1 }}>
                Interview Date and Time
              </Typography>
              <DatePicker
                selected={interviewDate}
                onChange={handleDateChange}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholderText="Select date and time"
                portalId="root"
                popperClassName="react-datepicker-popper"
                popperPlacement="bottom-start"
                wrapperClassName="date-picker-wrapper"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Interviewer</label>
              <select
                value={interviewerUserId}
                onChange={(e) => setInterviewerUserId(e.target.value)}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select an interviewer</option>
                {safeInterviewers.map((interviewer) => (
                  <option key={interviewer._id} value={interviewer._id}>
                    {interviewer.firstName} {interviewer.lastName} ({interviewer.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={() => setOpenScheduleDialog(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '100px',
              color: '#4B5563'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleScheduleInterview}
            variant="contained"
            disabled={!interviewDate || !interviewerUserId}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '100px',
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
      {/* Complete Interview Dialog */}
      <Dialog 
        open={openCompleteDialog} 
        onClose={handleCloseCompleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto',
          fontWeight: 700,
          fontSize: '1.5rem',
          color: 'rgba(255, 255, 255, 0.95)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          Complete Interview
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: 'transparent',
          pt: 3,
          pb: 2
        }}>
          <div className="space-y-4">
            <div>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                Interview Outcome
              </Typography>
              <div className="flex gap-3">
                <Button
                  variant={interviewOutcome === 'successful' ? 'contained' : 'outlined'}
                  onClick={() => setInterviewOutcome('successful')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    flex: 1,
                    py: 1.5,
                    backgroundColor: interviewOutcome === 'successful' ? 'rgba(76, 175, 80, 0.9)' : 'transparent',
                    color: interviewOutcome === 'successful' ? 'white' : 'rgba(76, 175, 80, 0.9)',
                    borderColor: 'rgba(76, 175, 80, 0.5)',
                    border: interviewOutcome === 'successful' ? 'none' : '2px solid',
                    '&:hover': {
                      backgroundColor: interviewOutcome === 'successful' ? 'rgba(76, 175, 80, 1)' : 'rgba(76, 175, 80, 0.1)',
                      borderColor: 'rgba(76, 175, 80, 0.7)'
                    }
                  }}
                >
                  Successful
                </Button>
                <Button
                  variant={interviewOutcome === 'unsuccessful' ? 'contained' : 'outlined'}
                  onClick={() => setInterviewOutcome('unsuccessful')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    flex: 1,
                    py: 1.5,
                    backgroundColor: interviewOutcome === 'unsuccessful' ? 'rgba(239, 68, 68, 0.9)' : 'transparent',
                    color: interviewOutcome === 'unsuccessful' ? 'white' : 'rgba(239, 68, 68, 0.9)',
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                    border: interviewOutcome === 'unsuccessful' ? 'none' : '2px solid',
                    '&:hover': {
                      backgroundColor: interviewOutcome === 'unsuccessful' ? 'rgba(239, 68, 68, 1)' : 'rgba(239, 68, 68, 0.1)',
                      borderColor: 'rgba(239, 68, 68, 0.7)'
                    }
                  }}
                >
                  Unsuccessful
                </Button>
              </div>
            </div>

            <div>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                Interview Notes
              </Typography>
              <TextField
                multiline
                rows={4}
                value={interviewNotes}
                onChange={handleNoteChange}
                fullWidth
                placeholder="Enter notes about the interview..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(100, 181, 246, 0.8)',
                      borderWidth: '2px'
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.4)',
                    opacity: 1
                  }
                }}
              />
            </div>

            <div>
              {interviewOutcome === 'successful' && (
                <div style={{ 
                  backgroundColor: 'rgba(76, 175, 80, 0.15)', 
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '8px'
                }}>
                  <Typography variant="body2" sx={{ color: 'rgba(76, 175, 80, 0.95)', fontWeight: 600, mb: 1 }}>
                    Upon completion:
                  </Typography>
                  <ul style={{ listStyleType: 'disc', marginLeft: '1.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <li style={{ marginBottom: '4px' }}>A user account will be created automatically with the counsellor's email address</li>
                    <li>Counsellor status will be set to "Onboarding"</li>
                  </ul>
                </div>
              )}
              
              {interviewOutcome === 'unsuccessful' && (
                <div style={{ 
                  backgroundColor: 'rgba(239, 68, 68, 0.15)', 
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '8px'
                }}>
                  <Typography variant="body2" sx={{ color: 'rgba(239, 68, 68, 0.95)', fontWeight: 500 }}>
                    As the counsellor was unsuccessful, they will be moved to Offboarding upon completion.
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ 
          padding: 3,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          gap: 2
        }}>
          <Button
            onClick={handleCloseCompleteDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              color: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCompleteInterview}
            variant="contained" 
            disabled={!interviewOutcome}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              backgroundColor: interviewOutcome === 'successful' ? 'rgba(76, 175, 80, 0.9)' : 'rgba(239, 68, 68, 0.9)',
              '&:hover': {
                backgroundColor: interviewOutcome === 'successful' ? 'rgba(76, 175, 80, 1)' : 'rgba(239, 68, 68, 1)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Complete Interview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Token Expiry Dialog */}
      <Dialog
        open={openExtendTokenDialog}
        onClose={() => setOpenExtendTokenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto',
          fontWeight: 700,
          fontSize: '1.5rem',
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          {counsellor?.applicationStatus === 'Pending Self-Completion' 
            ? 'Extend Onboarding Form Expiry' 
            : 'Extend Application Form Expiry'}
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: 'transparent',
          pt: 3,
          pb: 2
        }}>
          <div style={{ marginBottom: '16px' }}>
            <Typography variant="body2" className="theme-text-secondary" sx={{ mb: 2 }}>
              Select a new expiry date for the {counsellor?.applicationStatus === 'Pending Self-Completion' ? 'onboarding' : 'application'} form. The counsellor will be able to access the form until this date.
            </Typography>
            
            <Typography variant="subtitle2" className="theme-text-secondary" sx={{ mb: 1 }}>
              Current Expiry:
            </Typography>
            <Typography variant="body1" className="theme-text-primary" sx={{ fontWeight: 600, mb: 3 }}>
              {(() => {
                const expiryDate = counsellor?.applicationStatus === 'Pending Self-Completion' 
                  ? counsellor?.onboardingTokenExpiry 
                  : counsellor?.applicationExpiryDate;
                return expiryDate ? new Date(expiryDate).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                }) : 'N/A';
              })()}
            </Typography>

            <Typography variant="subtitle2" className="theme-text-secondary" sx={{ mb: 1 }}>
              New Expiry Date:
            </Typography>
            <DatePicker
              selected={newTokenExpiry}
              onChange={(date: Date | null) => {
                if (date) {
                  // Set time to 23:59:59 on the selected date
                  const endOfDay = new Date(date);
                  endOfDay.setHours(23, 59, 59, 999);
                  setNewTokenExpiry(endOfDay);
                } else {
                  setNewTokenExpiry(null);
                }
              }}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              className="w-full p-2 rounded-md"
              customInput={
                <TextField
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(59, 130, 246, 0.5)'
                      }
                    }
                  }}
                />
              }
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ 
          padding: 3,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          gap: 2
        }}>
          <Button
            onClick={() => {
              setOpenExtendTokenDialog(false);
              setNewTokenExpiry(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              color: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExtendToken}
            variant="contained"
            disabled={!newTokenExpiry}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              backgroundColor: 'rgba(59, 130, 246, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 1)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Extend Expiry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Counsellor Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => !suspending && setSuspendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            color: '#f8fafc',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#f8fafc' }}>
          Suspend Counsellor
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            Suspending locks {counsellor.firstName}'s account, cancels their future
            appointments and removes them from assignment. This is for compliance
            or investigation holds — not a permanent departure (use Archive for that).
          </Typography>
          <TextField
            select
            fullWidth
            label="Suspension Category"
            value={suspendCategory}
            onChange={(e) => setSuspendCategory(e.target.value as any)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { color: '#f8fafc' },
              '& .MuiInputLabel-root': { color: '#94a3b8' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <MenuItem value="dbs">DBS</MenuItem>
            <MenuItem value="insurance">Insurance</MenuItem>
            <MenuItem value="investigation">Investigation</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            required
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="e.g. DBS Update Service ID could not be verified"
            sx={{
              '& .MuiOutlinedInput-root': { color: '#f8fafc' },
              '& .MuiInputLabel-root': { color: '#94a3b8' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSuspendDialogOpen(false)}
            disabled={suspending}
            sx={{ color: '#94a3b8' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSuspend}
            disabled={suspending || !suspendReason.trim()}
            sx={{
              backgroundColor: '#f59e0b',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#d97706' }
            }}
          >
            {suspending ? 'Suspending…' : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </div>
    </ThemeBackground>
  );
};

export default CounsellorDetails;